'use client';

/**
 * The "Distribution" section of an app's Manage drawer (app-hosts plan G1,
 * 2026-09-24): where people install the app and how it reaches the stores.
 *
 *   - Install: the host's address with a copy button and the per-platform
 *     instructions (the browser's install control; Safari's Add to Home
 *     Screen).
 *   - Android: the PWABuilder report card for the address now, plus the
 *     package form. A package the owner lists here (name and signing
 *     certificate fingerprints) is served from the host's
 *     `/.well-known/assetlinks.json`, which is what makes Android trust the
 *     Trusted Web Activity for that address. Owner-only, through
 *     `/api/domains/:id/packages`; the platform's own `ai.robutler.*` family
 *     is refused server-side and named here so nobody types it twice.
 *   - Windows: only when the host is a custom domain, because the Microsoft
 *     Store accepts a packaged web app only from a domain its publisher
 *     owns; a platform-zone host is Robutler's.
 *   - iPhone and iPad: Home Screen from Safari, and one line about the
 *     Robutler host app, which is planned separately (plan G6).
 *
 * The host itself is looked up by content id (`/api/app-host/lookup`);
 * `verificationId` is in that answer only for the host's owner, and the
 * package form shows only then. An app without a host shows one sentence.
 * Never a self-hosted generator here (plan G5 is out of scope): the person
 * packages with PWABuilder under their own developer account.
 */

import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, ExternalLink, Loader2, Package, Trash2 } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';
// The rules only, never `app-packages` itself: that module holds the database
// client, and importing it here broke the `/workspace` build in the browser.
import {
  FINGERPRINT_RE,
  MAX_PACKAGES_PER_HOST,
  PACKAGE_NAME_RE,
  isReservedPackageName,
  normalizeFingerprint,
} from '@/lib/domains/app-package-rules';

interface HostInfo {
  host: string;
  origin: string;
  installUrl: string;
  platformZone: boolean;
  verificationId?: string;
}

interface PackageRow {
  packageName: string;
  fingerprints: string[];
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{children}</div>
  );
}

export function DistributionSection({ contentId }: { contentId: string }) {
  const t = useT();
  const [host, setHost] = useState<HostInfo | null | undefined>(undefined);
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [copied, setCopied] = useState(false);
  const [packageName, setPackageName] = useState('');
  const [fingerprintText, setFingerprintText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/app-host/lookup?contentId=${encodeURIComponent(contentId)}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      // `{ host: null }` with a 200 is the route's word for "no host"
      // (2026-09-24); a non-2xx is a failure, read the same way here.
      const info = res.ok ? ((await res.json()) as HostInfo | { host: null }) : null;
      if (!info || typeof info.host !== 'string') {
        setHost(null);
        return;
      }
      setHost(info as HostInfo);
      if (info.verificationId) {
        const p = await fetch(`/api/domains/${info.verificationId}/packages`, { credentials: 'same-origin', cache: 'no-store' });
        if (p.ok) setPackages((((await p.json()) as { packages?: PackageRow[] }).packages) ?? []);
      }
    } catch {
      setHost(null);
    }
  }, [contentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const copy = async () => {
    if (!host) return;
    try {
      await navigator.clipboard.writeText(host.installUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* the address is on screen to select */
    }
  };

  const fingerprints = fingerprintText
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const normalised = fingerprints.map(normalizeFingerprint);
  const formValid =
    PACKAGE_NAME_RE.test(packageName.trim()) &&
    !isReservedPackageName(packageName) &&
    fingerprints.length >= 1 &&
    fingerprints.length <= 4 &&
    normalised.every((f) => !!f && FINGERPRINT_RE.test(f));

  // The route answers with a `code` beside its English `error`; the person
  // reads the product's own copy for it, never the server string
  // (functional review, 2026-09-24). An unknown code, or a network failure,
  // reads as the generic failure.
  const PACKAGE_ERROR_KEY: Record<string, string> = {
    invalid_input: 'appHost.packageErrorInvalid',
    reserved: 'appHost.packageErrorReserved',
    too_many: 'appHost.packageErrorTooMany',
    unverified: 'appHost.packageErrorUnverified',
    not_found: 'appHost.packageErrorNotFound',
  };
  const packageFailure = (code: string | undefined, message: string) =>
    Object.assign(new Error(message), { code: code ?? null });
  const packageErrorText = (e: unknown): string => {
    const code = (e as { code?: string | null } | null)?.code ?? null;
    const key = code ? PACKAGE_ERROR_KEY[code] : undefined;
    return key ? t(key, { max: MAX_PACKAGES_PER_HOST }) : t('appHost.packageFailed');
  };

  const addPackage = async () => {
    if (!host?.verificationId || !formValid || busy) return;
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch(`/api/domains/${host.verificationId}/packages`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ package_name: packageName.trim(), sha256_fingerprints: fingerprints }),
      });
      const body = (await res.json().catch(() => null)) as { packages?: PackageRow[]; error?: string; code?: string } | null;
      if (!res.ok) throw packageFailure(body?.code, body?.error || `Failed (${res.status})`);
      setPackages(body?.packages ?? []);
      setPackageName('');
      setFingerprintText('');
      setNote({ kind: 'ok', text: t('appHost.packageSaved') });
    } catch (e) {
      setNote({ kind: 'error', text: packageErrorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const removePackage = async (name: string) => {
    if (!host?.verificationId || busy) return;
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch(`/api/domains/${host.verificationId}/packages`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ package_name: name }),
      });
      const body = (await res.json().catch(() => null)) as { packages?: PackageRow[]; error?: string; code?: string } | null;
      if (!res.ok) throw packageFailure(body?.code, body?.error || `Failed (${res.status})`);
      setPackages(body?.packages ?? []);
      setNote({ kind: 'ok', text: t('appHost.removed') });
    } catch (e) {
      setNote({ kind: 'error', text: packageErrorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'w-full rounded-lg border border-border/60 bg-background px-3 py-1.5 text-sm outline-none focus:border-primary/60';
  const reportCard = host ? `https://www.pwabuilder.com/reportcard?site=${encodeURIComponent(host.installUrl)}` : '';

  return (
    <div className="mt-6" data-testid="manage-distribution">
      <SectionLabel>{t('appHost.distribution')}</SectionLabel>
      <p className="mb-3 text-[11px] leading-snug text-muted-foreground">{t('appHost.distributionIntro')}</p>

      {host === undefined ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : host === null ? (
        <p className="text-[12px] text-muted-foreground">{t('appHost.noHost')}</p>
      ) : (
        <div className="space-y-5">
          {/* The address */}
          <div>
            <div className="mb-1 text-[12px] font-medium">{t('appHost.hostAddress')}</div>
            <div className="flex items-center gap-2">
              <a
                href={host.installUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate font-mono text-[12px] text-primary hover:underline"
                data-testid="distribution-host"
              >
                {host.installUrl}
              </a>
              <button
                type="button"
                onClick={() => void copy()}
                aria-label={t('appHost.copyLink')}
                title={t('appHost.copyLink')}
                className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>

          {/* Web install */}
          <div>
            <div className="mb-1 text-[12px] font-medium">{t('appHost.installOnWeb')}</div>
            <p className="text-[11px] leading-snug text-muted-foreground">{t('appHost.installOnWebHelp')}</p>
          </div>

          {/* Android */}
          <div>
            <div className="mb-1 text-[12px] font-medium">{t('appHost.android')}</div>
            <p className="mb-2 text-[11px] leading-snug text-muted-foreground">{t('appHost.androidHelp')}</p>
            <a
              href={reportCard}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('appHost.androidReportCard')}
            </a>

            {host.verificationId ? (
              <div className="mt-3 space-y-2">
                <div className="text-[11px] font-medium text-muted-foreground">{t('appHost.androidPackages')}</div>
                {packages.length === 0 ? (
                  <p className="text-[12px] text-muted-foreground">{t('appHost.androidNoPackages')}</p>
                ) : (
                  <ul className="space-y-1">
                    {packages.map((p) => (
                      <li key={p.packageName} className="flex items-center gap-2 rounded-lg border border-border/60 px-2 py-1.5">
                        <Package className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-mono text-[12px]">{p.packageName}</div>
                          <div className="truncate text-[10px] text-muted-foreground">
                            {p.fingerprints.map((f) => f.slice(0, 23)).join(', ')}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => void removePackage(p.packageName)}
                          disabled={busy}
                          aria-label={t('appHost.removePackage', { name: p.packageName })}
                          title={t('appHost.removePackage', { name: p.packageName })}
                          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <label className="block text-[11px] text-muted-foreground">
                  {t('appHost.packageName')}
                  <input
                    value={packageName}
                    onChange={(e) => setPackageName(e.target.value)}
                    placeholder={t('appHost.packageNamePlaceholder')}
                    className={cn(inputCls, 'mt-1 font-mono')}
                    data-testid="distribution-package-name"
                  />
                </label>
                <label className="block text-[11px] text-muted-foreground">
                  {t('appHost.fingerprints')}
                  <textarea
                    value={fingerprintText}
                    onChange={(e) => setFingerprintText(e.target.value)}
                    rows={3}
                    className={cn(inputCls, 'mt-1 font-mono text-[11px]')}
                    data-testid="distribution-fingerprints"
                  />
                  <span className="mt-1 block leading-snug">{t('appHost.fingerprintsHelp')}</span>
                </label>
                <button
                  type="button"
                  onClick={() => void addPackage()}
                  disabled={!formValid || busy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
                  data-testid="distribution-add-package"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                  {t('appHost.addPackage')}
                </button>
                {note && (
                  <p className={cn('text-[11px]', note.kind === 'ok' ? 'text-emerald-600' : 'text-red-500')}>{note.text}</p>
                )}
              </div>
            ) : null}
          </div>

          {/* Windows */}
          <div>
            <div className="mb-1 text-[12px] font-medium">{t('appHost.windows')}</div>
            <p className="text-[11px] leading-snug text-muted-foreground">
              {host.platformZone ? t('appHost.windowsOwnDomainOnly') : t('appHost.windowsHelp')}
            </p>
          </div>

          {/* iOS */}
          <div>
            <div className="mb-1 text-[12px] font-medium">{t('appHost.ios')}</div>
            <p className="text-[11px] leading-snug text-muted-foreground">{t('appHost.iosHelp')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
